import * as React from 'react';
import MuiSelect, { SelectProps } from '@mui/material/Select';
import Input from '@mui/material/Input';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { useTranslation } from 'react-i18next';

import { PassengerFieldOption } from '../../../types';
import { useEffect } from 'react';
import { MUIChangeEvent } from '../../../../types';
import cn from 'classnames';
import { useTheme } from '../../../../theme';
import { MenuProps } from '@mui/material';

interface ClassKeys {
	label?: string;
	label_error?: string;
	label_focused?: string;
	input?: string;
	input_focused?: string;
	input_error?: string;
	input_disabled?: string;
}

export type SelectRenderers = Partial<{
	renderValue: (value: SelectProps['value']) => React.ReactNode;
	renderOption: (option: PassengerFieldOption) => React.ReactNode;
}>;

export interface Props {
	options: PassengerFieldOption[];
	name: string;
	label: string;
	classes?: ClassKeys;
	onChange?: (key: string, event: React.ChangeEvent<HTMLSelectElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
	className?: string;
	value?: string;
	error?: string;
	translateValue?: boolean;
	disabled?: boolean;
	readonly?: boolean;
	variant?: 'outlined' | 'filled' | 'standard';
	selectFirstOptionByDefault?: boolean;
	disableAutoSelect?: boolean;
	menuProps?: Partial<MenuProps>;
}

export const Select: React.FunctionComponent<Props & SelectRenderers> = props => {
	const [value, setValue] = React.useState(props.value ? props.value : '');
	const { t } = useTranslation('Passenger');
	const { Input: inputCss } = useTheme('Input');
	const { Select: css } = useTheme('Passenger');

	const variant = props.variant || 'filled';

	const onChange = (e: MUIChangeEvent) => {
		if (typeof e.target.value === 'string') {
			const selected = e.target.value;

			setValue(selected);

			props.onChange?.(e.target.value, (e as unknown) as React.ChangeEvent<HTMLSelectElement>);
		}
	};

	useEffect(() => {
		setValue(props.value);
	}, [props.value]);

	useEffect(() => {
		if (
			props.options.length &&
			!props.value &&
			!props.disableAutoSelect &&
			(props.options.length === 1 || props.selectFirstOptionByDefault)
		) {
			const optionValue = props.options[0].key;
			const changeEvent = {
				target: {
					value: optionValue
				}
			} as React.ChangeEvent<HTMLSelectElement>;

			setValue(optionValue);

			props?.onChange(optionValue, changeEvent);
		}
	}, [props.options, props.selectFirstOptionByDefault, props.disableAutoSelect, props.value]);

	return (
		<FormControl className={cn(props.className)} error={!!props.error} variant={variant}>
			<InputLabel
				htmlFor={`select-${props.name}`}
				classes={{
					root: cn(inputCss.textField__root, props.classes?.label, css.label, {
						[inputCss.textField_standard]: variant === 'standard'
					}),
					focused: cn(inputCss.textField, inputCss.textField_focused, props.classes?.label_focused),
					error: cn(inputCss.label_error, props.classes?.label_error),
					shrink: inputCss.label_shrink,
					outlined: inputCss.textField_outlined,
					filled: inputCss.textField_filled
				}}
			>
				{props.label}
			</InputLabel>

			<MuiSelect
				renderValue={props.renderValue}
				value={value}
				name={props.name}
				MenuProps={props.menuProps}
				input={
					<Input
						classes={{
							disabled: cn(inputCss.disabled, props.classes?.input_disabled),
							// underline: css.select,
							root: cn(
								css.input,
								inputCss.input,
								inputCss.real_input,
								{
									[inputCss.input_filled]: variant === 'filled',
									[inputCss.input_outline]: variant === 'outlined',
									[inputCss.input_standard]: variant === 'standard',
									[css.input_standard]: variant === 'standard',
									[css.input_readonly]: props.readonly
								},
								props.classes?.input
							),
							error: cn(css.error, props.classes?.input_error),
							focused: cn(inputCss.input_focused, props.classes?.input_focused),
							underline: inputCss.underline,
							input: cn(css.real_input, { [css.real_input_noLabel]: !props.label })
						}}
						name={props.name}
						id={`select-${props.name}`}
						disableUnderline={variant !== 'standard'}
						readOnly={props.readonly}
					/>
				}
				classes={{
					icon: css.icon
				}}
				disabled={props.disabled}
				onChange={onChange}
				onBlur={props.onBlur}
				readOnly={props.readonly}
			>
				{props.options.map(option => {
					if (props.renderOption) {
						return props.renderOption(option);
					} else {
						return (
							<MenuItem
								key={option.key}
								value={option.key}
								className={css.option}
								disabled={option.disabled}
							>
								{props.translateValue ? t(option.value) : option.value}
							</MenuItem>
						);
					}
				})}
			</MuiSelect>

			{props.error && <FormHelperText classes={{ root: css.helperText }}>{props.error}</FormHelperText>}
		</FormControl>
	);
};
